import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LeadTempAssignmentStatusTypeInput,
  NextLeadAssignmentResponseNode,
} from '@app/generated/graphql';
import { LocalStorageService } from '@voyagu/radar-design-system';
import { BehaviorSubject, interval, map, takeWhile } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { getTitle, setTitle } from '@app/shared/components/page-title-abstract';

@Component({
  selector: 'lead-boost',
  templateUrl: './lead-boost.component.html',
  styleUrls: ['./lead-boost.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadBoostComponent implements OnInit, OnDestroy {
  LeadTempAssignmentStatusTypeInput = LeadTempAssignmentStatusTypeInput;

  destroyRef = inject(DestroyRef);
  localStorageService = inject(LocalStorageService);

  duration = 100;

  progress$ = new BehaviorSubject(0);

  loading: boolean;

  collapsed: boolean;

  currentTitle = getTitle();

  popupOpenedAt: Date;

  soundTimer: number | null = null;

  @Input() nextLeadAssignment: NextLeadAssignmentResponseNode;
  @Output() completeLeadAssignment = new EventEmitter();

  ngOnInit() {
    this.popupOpenedAt = new Date();

    this.initInterval();
    this.tryPlayAudio();

    setTitle('⚠️ New Lead');
  }

  initInterval() {
    const assignedAt = new Date(this.nextLeadAssignment.assignedAt as Date);
    const currentDate = new Date();

    const diff = currentDate.getTime() - assignedAt.getTime();
    const diffInSeconds = Math.floor(diff / 1000);

    interval(1000)
      .pipe(
        map((i) => diffInSeconds + i),
        takeWhile((i) => i <= this.duration),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((i) => this.progress$.next(i));
  }

  tryPlayAudio() {
    const assignmentId = this.nextLeadAssignment.assignmentId;

    if (!assignmentId || this.isSoundPlayed(assignmentId)) return;

    if (hasUserActivation()) {
      this.claimSound(assignmentId);
      return;
    }

    // Tabs without a user gesture usually cannot autoplay, so give activated tabs the first try.
    this.soundTimer = window.setTimeout(
      () => this.claimSound(assignmentId),
      INACTIVE_TAB_SOUND_DELAY_MS,
    );
  }

  isSoundPlayed(assignmentId: string) {
    return this.localStorageService.getItem(ASSIGNMENT_ID_KEY) === assignmentId;
  }

  claimSound(assignmentId: string) {
    this.soundTimer = null;

    if (this.isSoundPlayed(assignmentId)) return;

    if (!navigator.locks) {
      this.playSound(assignmentId);
      return;
    }

    // Hold the lock long enough for the localStorage write to reach the other tabs.
    void navigator.locks.request(
      `lead-boost-sound-${assignmentId}`,
      { ifAvailable: true },
      (lock) => {
        if (!lock || this.isSoundPlayed(assignmentId)) return;

        this.playSound(assignmentId);

        return new Promise((resolve) => window.setTimeout(resolve, SOUND_LOCK_HOLD_MS));
      },
    );
  }

  playSound(assignmentId: string) {
    if (hasUserActivation()) {
      this.localStorageService.setItem(ASSIGNMENT_ID_KEY, assignmentId);
    }

    void this.playAudio().then((played) => {
      if (played) {
        this.localStorageService.setItem(ASSIGNMENT_ID_KEY, assignmentId);
      }
    });
  }

  playAudio() {
    const audio = new Audio();
    audio.src = '../../../../assets/sounds/notification.mp3';
    audio.load();

    return audio
      .play()
      .then(() => {
        this.captureSoundDelayIfNeeded();

        return true;
      })
      .catch((error) => {
        Sentry.captureMessage('Leadboost sound error', {
          level: 'error',
          extra: {
            datetime: JSON.stringify(new Date()),
            assignmentId: this.nextLeadAssignment?.assignmentId,
            error: JSON.stringify(error.message || error.toString()),
          },
        });

        console.error('Leadboost: ', error);

        return false;
      });
  }

  captureSoundDelayIfNeeded() {
    const soundPlayedAt = new Date();
    const diffInMs = soundPlayedAt.getTime() - this.popupOpenedAt.getTime();

    if (diffInMs <= 1000) return;

    Sentry.captureMessage('Leadboost sound delayed after popup opened', {
      extra: {
        popupOpenedAt: JSON.stringify(this.popupOpenedAt),
        soundPlayedAt: JSON.stringify(soundPlayedAt),
        diffInMs,
        assignmentId: this.nextLeadAssignment?.assignmentId,
      },
    });
  }

  submit(status: LeadTempAssignmentStatusTypeInput) {
    const input = {
      assignmentId: this.nextLeadAssignment.assignmentId,
      status,
    };

    this.loading = true;

    this.completeLeadAssignment.emit({ input });

    setTitle(this.currentTitle);
  }

  ngOnDestroy() {
    if (this.soundTimer !== null) {
      window.clearTimeout(this.soundTimer);
    }

    setTitle(this.currentTitle);
  }
}

const ASSIGNMENT_ID_KEY = 'assignment_id';
const INACTIVE_TAB_SOUND_DELAY_MS = 500;
const SOUND_LOCK_HOLD_MS = 2000;

function hasUserActivation() {
  return !!navigator.userActivation?.hasBeenActive;
}
